import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import particleSource from '@/assets/images/particle-default-source.jpg';
import styles from './index.module.scss';

const REFERENCE_PARTICLE_COUNT = 70000;
const REDUCED_PARTICLE_COUNT = 40000;
const IMAGE_RESOLUTION = 200;
const POINT_SIZE = 110;
const FRAME_INTERVAL = 1000 / 30;

const vertexShader = `
  uniform float uTime;
  uniform float uMorph;
  uniform float uPointSize;
  attribute vec3 targetPosition;
  attribute vec3 targetColor;
  attribute vec3 color;
  varying vec3 vColor;
  varying float vDistance;

  void main() {
    vColor = mix(color, targetColor, uMorph);
    vec3 pos = mix(position, targetPosition, uMorph);
    float noise = sin(uTime * 1.5 + position.x * 0.3) * cos(uTime * 1.5 + position.y * 0.3);
    pos += normalize(pos) * noise * (0.2 * (1.0 - uMorph));
    pos.x += sin(uTime * 0.3 + position.z) * 0.1;
    pos.y += cos(uTime * 0.3 + position.x) * 0.1;

    vec4 viewPosition = modelViewMatrix * vec4(pos, 1.0);
    float distanceFromCenter = length(pos);
    vDistance = distanceFromCenter;
    gl_PointSize = (uPointSize / -viewPosition.z) * (1.2 + sin(uTime * 3.0 + distanceFromCenter * 0.15) * 0.5);
    gl_Position = projectionMatrix * viewPosition;
  }
`;

const fragmentShader = `
  uniform float uTime;
  varying vec3 vColor;
  varying float vDistance;

  void main() {
    float distanceToCenter = distance(gl_PointCoord, vec2(0.5));
    if (distanceToCenter > 0.5) discard;
    float strength = pow(1.0 - distanceToCenter * 2.0, 1.6);
    vec3 finalColor = vColor * 2.0;
    float alpha = strength * (0.8 + sin(vDistance * 0.3 + uTime) * 0.2);
    gl_FragColor = vec4(finalColor, alpha);
  }
`;

interface ParticleBackgroundProps {
  active: boolean;
}

interface SampledImagePoint {
  position: [number, number, number];
  color: [number, number, number];
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load particle source: ${src}`));
    image.src = src;
  });
}

function sampleImage(image: HTMLImageElement): SampledImagePoint[] {
  const canvas = document.createElement('canvas');
  canvas.width = IMAGE_RESOLUTION;
  canvas.height = IMAGE_RESOLUTION;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Canvas 2D context is unavailable');

  const aspect = image.width / image.height;
  const drawWidth = aspect > 1 ? IMAGE_RESOLUTION : IMAGE_RESOLUTION * aspect;
  const drawHeight = aspect > 1 ? IMAGE_RESOLUTION / aspect : IMAGE_RESOLUTION;
  context.fillStyle = '#000';
  context.fillRect(0, 0, IMAGE_RESOLUTION, IMAGE_RESOLUTION);
  context.drawImage(
    image,
    (IMAGE_RESOLUTION - drawWidth) / 2,
    (IMAGE_RESOLUTION - drawHeight) / 2,
    drawWidth,
    drawHeight,
  );

  const pixels = context.getImageData(0, 0, IMAGE_RESOLUTION, IMAGE_RESOLUTION).data;
  const points: SampledImagePoint[] = [];

  for (let y = 0; y < IMAGE_RESOLUTION; y += 1) {
    for (let x = 0; x < IMAGE_RESOLUTION; x += 1) {
      const pixelIndex = (y * IMAGE_RESOLUTION + x) * 4;
      const red = pixels[pixelIndex];
      const green = pixels[pixelIndex + 1];
      const blue = pixels[pixelIndex + 2];
      const brightness = (red + green + blue) / 3;
      if (brightness <= 15) continue;

      points.push({
        position: [
          (x / IMAGE_RESOLUTION - 0.5) * 38,
          (0.5 - y / IMAGE_RESOLUTION) * 38,
          ((red + green + blue) / 765 - 0.5) * 12,
        ],
        color: [red / 255, green / 255, blue / 255],
      });
    }
  }

  return points;
}

function getParticleCount() {
  return (navigator.hardwareConcurrency || 8) >= 8 ? REFERENCE_PARTICLE_COUNT : REDUCED_PARTICLE_COUNT;
}

const ParticleBackground: React.FC<ParticleBackgroundProps> = ({ active }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(active);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let animationFrame = 0;
    let resizeObserver: ResizeObserver | undefined;
    let renderer: THREE.WebGLRenderer | undefined;
    let geometry: THREE.BufferGeometry | undefined;
    let material: THREE.ShaderMaterial | undefined;

    const initialize = async () => {
      const image = await loadImage(particleSource);
      if (disposed) return;

      const sampledPoints = sampleImage(image);
      if (!sampledPoints.length) throw new Error('Particle source contains no visible pixels');

      const particleCount = getParticleCount();
      const positions = new Float32Array(particleCount * 3);
      const targetPositions = new Float32Array(particleCount * 3);
      const colors = new Float32Array(particleCount * 3);
      const targetColors = new Float32Array(particleCount * 3);
      const green = new THREE.Color(0x00ff66);
      const white = new THREE.Color(0xffffff);

      for (let index = 0; index < particleCount; index += 1) {
        const offset = index * 3;
        const vertical = (Math.random() - 0.5) * 5;
        const angle = Math.random() * Math.PI * 2;
        const radiusBase = 0.4 + Math.pow(Math.abs(vertical), 2.4);
        const radius = radiusBase * (0.75 + Math.random() * 0.55);
        const initialColor = Math.random() > 0.7 ? green : white;
        const sampledPoint = sampledPoints[index % sampledPoints.length];

        positions[offset] = radius * Math.cos(angle) * 2.9;
        positions[offset + 1] = vertical * 7.5;
        positions[offset + 2] = radius * Math.sin(angle) * 2.9;
        colors[offset] = initialColor.r;
        colors[offset + 1] = initialColor.g;
        colors[offset + 2] = initialColor.b;

        targetPositions[offset] = sampledPoint.position[0] + (Math.random() - 0.5) * 0.4;
        targetPositions[offset + 1] = sampledPoint.position[1] + (Math.random() - 0.5) * 0.4;
        targetPositions[offset + 2] = sampledPoint.position[2] + (Math.random() - 0.5) * 1.5;
        targetColors[offset] = sampledPoint.color[0];
        targetColors[offset + 1] = sampledPoint.color[1];
        targetColors[offset + 2] = sampledPoint.color[2];
      }

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(35, 958 / 764, 0.1, 1000);
      camera.position.z = 45;

      geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('targetPosition', new THREE.BufferAttribute(targetPositions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geometry.setAttribute('targetColor', new THREE.BufferAttribute(targetColors, 3));

      material = new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        uniforms: {
          uTime: { value: 0 },
          uMorph: { value: 0 },
          uPointSize: { value: POINT_SIZE },
        },
      });

      const particles = new THREE.Points(geometry, material);
      scene.add(particles);

      renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
      renderer.setClearColor(0x000000, 1);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
      container.appendChild(renderer.domElement);

      const resize = () => {
        if (!renderer) return;
        const width = Math.max(1, container.clientWidth);
        const height = Math.max(1, container.clientHeight);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height, false);
      };

      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(container);
      resize();

      let time = 0;
      let morph = 0;
      let lastRenderedAt = 0;
      const animate = (now: number) => {
        if (disposed || !renderer || !material) return;

        if (activeRef.current && !document.hidden && now - lastRenderedAt >= FRAME_INTERVAL) {
          const deltaSeconds = lastRenderedAt ? Math.min((now - lastRenderedAt) / 1000, 0.05) : FRAME_INTERVAL / 1000;
          lastRenderedAt = now;
          time += deltaSeconds * 0.48;
          morph += (1 - morph) * 0.1;
          particles.rotation.y += deltaSeconds * 0.15;
          particles.rotation.z += deltaSeconds * 0.06;
          particles.rotation.x = Math.sin(time * 0.15) * 0.12;
          material.uniforms.uTime.value = time;
          material.uniforms.uMorph.value = morph;
          renderer.render(scene, camera);
        }

        animationFrame = requestAnimationFrame(animate);
      };

      animationFrame = requestAnimationFrame(animate);
    };

    initialize().catch(error => {
      console.error('Failed to initialize particle background:', error);
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      geometry?.dispose();
      material?.dispose();
      renderer?.dispose();
      renderer?.domElement.remove();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`${styles.particleBackground} ${active ? styles.active : styles.inactive}`}
      aria-hidden="true"
    />
  );
};

export default ParticleBackground;
